import { h, Component } from 'preact';
import { joinClasses } from 'utils/utils';
import { createNewPass, updatePassWithBadge, formatDownloadLink } from 'helpers/passportApi';
import { generateQRCode, loadImage } from 'helpers/index';
import { AppleLogo, GoogleLogo } from 'helpers/svgs';
import copy from 'helpers/copy.js';
import { checkIfWebView } from 'utils/webView';
import Store from 'store/store';
import HTML from 'components/core/html/html.js';
import s from 'components/bolt/airport/popUpWindows/passport/passport.sss';

// stages
const INTRO = 'intro';

// Theatres
const THEATRES_INTRO = 'theatresIntro';
const THEATRES_INPUT = 'theatresInput';
const THEATRES_CONFIRMATION = 'theatresConfirmation';

// Home Ent
const HOME_ENT_INPUT = 'homeEntInput';
const HOME_ENT_CONFIRMATION = 'homeEntConfirmation';

const CODE_LIMIT = 8;
const PASS_ID_LIMIT = 12;

export default class Passport extends Component {
	constructor(props) {
		super(props);
		const showHomeEnt = Store.get()?.appData?.options?.enableHomeEntPass;
		this.startOver = this.startOver.bind(this);
		this.onChooseNewClick = this.onChooseNewClick.bind(this);
		this.onChooseExistingClick = this.onChooseExistingClick.bind(this);
		this.onChoosePassportClick = this.onChoosePassportClick.bind(this);
		this.onChooseHomeEntClick = this.onChooseHomeEntClick.bind(this);
		this.onCodeInput = this.onCodeInput.bind(this);
		this.onPassIdInput = this.onPassIdInput.bind(this);
		this.initialState = {
			stage: showHomeEnt ? INTRO : THEATRES_INTRO,
			existingTraveler: false,
			walletPlatform: '',
			isWebView: false,
			confirmationCodeInput: '',
			passIdInput: '',
			confirmationCodeInputFocused: false,
			passIdInputFocused: false,
			loading: false,
			hasWrongPassFlow: false,
			hasConfirmationCodeError: false,
			hasPassIdError: false,
			hasMaximumBadgeError: false,
			newPassDownloadLink: '',
			badgeSuccessMaximum: false,
			showLinkCopiedConfirmation: false,
			apiErrorMessage: false,
		};
		this.state = { ...this.initialState };
	}

	componentDidMount() {
		const { client } = this.props;
		const walletPlatform = client.device === 'desktop' ? 'ios' : client.isIOS ? 'ios' : 'android';
		this.setState({ walletPlatform, isWebView: checkIfWebView() });
	}

	onChooseNewClick() {
		this.setState({ stage: THEATRES_INPUT });
	}

	onChooseExistingClick() {
		this.setState({ stage: THEATRES_INPUT, existingTraveler: true });
	}

	onChoosePassportClick() {
		const data = Store.get();
		Store.set({ ...data, popUpTitleOverride: copy?.$passport?.routePassportTitle || undefined });
		this.setState({ stage: THEATRES_INTRO });
	}

	onChooseHomeEntClick() {
		console.log(copy);
		const data = Store.get();
		Store.set({ ...data, popUpTitleOverride: copy?.$passport?.routeSouvenirTitle || undefined });
		this.setState({ stage: HOME_ENT_INPUT });
	}

	changeWalletPlatform(platform) {
		this.setState({ walletPlatform: platform });
	}

	startOver() {
		const { walletPlatform, isWebView } = this.state;
		const data = Store.get();
		Store.set({ ...data, popUpTitleOverride: undefined });
		this.setState({
			...this.initialState,
			walletPlatform,
			isWebView,
		});
	}

	onCodeInputComplete() {
		const { existingTraveler, confirmationCodeInput, passIdInput, stage } = this.state;
		if (!existingTraveler && confirmationCodeInput.length === CODE_LIMIT) {
			this.createNewPass();
			return;
		}
		if (existingTraveler && confirmationCodeInput.length === CODE_LIMIT && passIdInput.length === PASS_ID_LIMIT) {
			this.updatePassWithBadge();
		}
	}

	onPassIdInputComplete() {
		const { confirmationCodeInput, passIdInput } = this.state;
		if (confirmationCodeInput.length !== CODE_LIMIT || passIdInput.length !== PASS_ID_LIMIT) {
			return;
		}
		this.updatePassWithBadge();
	}

	onCodeInput(e) {
		let value = e.target.value?.toUpperCase();
		const { stage } = this.state;
		const lastChar = value[value.length - 1] || '';
		if (!lastChar.match(/[A-Z]|\d/)) {
			value = value.slice(0, -1);
			e.target.value = value;
		}
		if (value.length > CODE_LIMIT) {
			value = value.slice(0, CODE_LIMIT);
			e.target.value = value;
			return;
		}
		this.setState({
			confirmationCodeInput: value,
			hasConfirmationCodeError: false,
		});
		if (value.length === CODE_LIMIT) {
			this.onCodeInputComplete();
		}
	}

	onPassIdInput(e) {
		let value = e.target.value;
		const lastChar = value[value.length - 1] || '';
		if (!lastChar.match(/\d/)) {
			value = value.slice(0, -1);
			e.target.value = value;
		}
		if (value.length > PASS_ID_LIMIT) {
			value = value.slice(0, PASS_ID_LIMIT);
			e.target.value = value;
			return;
		}
		this.setState({
			passIdInput: value,
			hasPassIdError: false,
		});
		if (value.length === PASS_ID_LIMIT) {
			this.onPassIdInputComplete();
		}
	}

	checkInLocalStorage(code) {
		const redemption = JSON.parse(localStorage.getItem(`passportRedemption${code}`));
		if (redemption?.downloadLink && Date.now() - redemption.timeStamp < 1000 * 60 * 60 * 20) {
			return { passId: redemption.passId, downloadLink: redemption.downloadLink };
		}
		return false;
	}

	saveInLocalStorage(code, passId, downloadLink) {
		const existingRecord = this.checkInLocalStorage(code);
		if (existingRecord) {
			return;
		}
		localStorage.setItem(`passportRedemption${code}`, JSON.stringify({ code, passId, downloadLink, timeStamp: Date.now() }));
	}

	async createNewPass() {
		const { confirmationCodeInput, walletPlatform, existingTraveler, stage } = this.state;
		const { client, imageRegionRoot } = this.props;
		this.setState({ loading: true });
		this.$codeInput.blur();

		let res = {};
		const existingRecord = this.checkInLocalStorage(confirmationCodeInput);
		if (existingRecord) {
			res = existingRecord;
		} else {
			// If stage is HOME_ENT_INPUT, pass flag to use different endpoint for redemption
			res = await createNewPass(confirmationCodeInput, walletPlatform, stage === HOME_ENT_INPUT);
		}

		const isTheatreRedemptionError = (res.message && res.message?.includes('redemption code')) || res.message?.includes('code must match pattern');
		if (isTheatreRedemptionError || res.message) {
			if (isTheatreRedemptionError) {
				this.setState({ hasConfirmationCodeError: true, loading: false, apiErrorMessage: false });
			} else {
				this.setState({
					hasConfirmationCodeError: false,
					hasWrongPassFlow: true,
					loading: false,
					apiErrorMessage: res.message || 'An error occurred. Please try again.',
				});
			}
			return;
		}
		if (res.passId && res.downloadLink) {
			const isDesktop = client.device === 'desktop';
			if (isDesktop) {
				const qrData = await generateQRCode(formatDownloadLink(res.downloadLink));
				const qrContext = this.$qrCanvas.getContext('2d');
				this.$qrCanvas.width = qrData.width;
				this.$qrCanvas.height = qrData.height;
				qrContext.drawImage(qrData, 0, 0);
			}
			const showWalletLinks = !existingTraveler && !isDesktop;
			if (showWalletLinks) {
				if (walletPlatform === 'ios' && !this.iosWalletBtnLoaded) {
					await loadImage(`${imageRegionRoot}/airport/add-to-apple-wallet.webp`);
					this.iosWalletBtnLoaded = true;
				} else if (walletPlatform === 'android' && !this.androidWalletBtnLoaded) {
					await loadImage(`${imageRegionRoot}/airport/add-to-google-wallet.webp`);
					this.androidWalletBtnLoaded = true;
				}
			}
			// Use previous stage to infer confirmation page. Feels slightly brittle but saves complication
			this.setState({
				stage: stage === THEATRES_INPUT ? THEATRES_CONFIRMATION : HOME_ENT_CONFIRMATION,
				loading: false,
				newPassDownloadLink: formatDownloadLink(res.downloadLink),
			});
			this.saveInLocalStorage(confirmationCodeInput, res.passId, formatDownloadLink(res.downloadLink));
		}
	}

	async updatePassWithBadge() {
		const { confirmationCodeInput, passIdInput } = this.state;
		this.setState({ loading: true });
		this.$codeInput.blur();
		this.$passIdInput.blur();
		const res = await updatePassWithBadge(confirmationCodeInput, passIdInput);
		if (res.message?.includes('redemption code') || res.message?.includes('code must match pattern')) {
			this.setState({ hasConfirmationCodeError: true, loading: false });
		}
		if (res.message?.includes('Pass ID')) {
			this.setState({ hasPassIdError: true, loading: false });
		}
		if (res.message?.includes('Maximum reached, pass not updated')) {
			this.setState({ hasPassIdError: true, hasMaximumBadgeError: true, loading: false });
		}
		if (res.passId && res.message?.includes('Pass updated successfully')) {
			this.setState({ stage: THEATRES_CONFIRMATION, loading: false, badgeSuccessMaximum: res.message.includes('Maximum reached') });
		}
	}

	onAddToWalletClick({ e, walletPlatform, useCopyDownloadLink, link }) {
		if (useCopyDownloadLink) {
			e.preventDefault();
			navigator.clipboard.writeText(link);
			this.setState({ showLinkCopiedConfirmation: true });
		}
		this.trackAddToWalletClick(walletPlatform);
	}

	trackAddToWalletClick(walletPlatform) {
		const isPassport = this.state.stage === THEATRES_CONFIRMATION;
		Store.emit('CUSTOM_EVENT', {
			action: `${isPassport ? 'pass' : 'card'}-download-click`,
			category: isPassport ? 'boarding-pass' : 'collectors-card',
			label: walletPlatform === 'ios' ? 'apple' : 'google',
		});
	}

	renderRestartButton({ copy }) {
		const { imageRoot } = this.props;
		const hide = this.state.stage === INTRO;

		return (
			<button class={joinClasses(s.restartBtn, hide && s.hide)} onClick={this.startOver}>
				<span>{copy.restart}</span>
				<img src={`${imageRoot}/icons/restart.webp`} />
			</button>
		);
	}

	renderIntro({ copy }) {
		return (
			<div class={joinClasses(s.content, s.intro, s.routeChoice)}>
				<HTML class={s.introCopy} content={copy?.intro} markdown />
				<div class={s.introButtons}>
					<div class={s.chooseNewContainer}>
						<button class={s.chooseNewBtn} onClick={this.onChoosePassportClick}>
							{copy.routePassportBtn}
						</button>
						<HTML class={s.introBtnCentred} content={copy.routePassportBlurb} markdown />
					</div>
					<div class={s.chooseNewContainer}>
						<button class={s.chooseExistingBtn} onClick={this.onChooseHomeEntClick}>
							{copy.routeHomeEntBtn}
						</button>
						<HTML class={s.introBtnCentred} content={copy.routeHomeEntBlurb} markdown />
					</div>
				</div>
			</div>
		);
	}

	renderTheatreIntro({ copy }) {
		return (
			<div class={joinClasses(s.content, s.intro)}>
				<HTML class={s.introCopy} content={copy?.theatreIntro} markdown />
				<div class={s.introButtons}>
					<div class={s.chooseNewContainer}>
						<button class={s.chooseNewBtn} onClick={this.onChooseNewClick}>
							{copy.newPassBtn}
						</button>
						<span class={s.introBtnExplainer}>{copy.newPassDescription}</span>
					</div>
					<div class={s.chooseExistingContainer}>
						<button class={s.chooseExistingBtn} onClick={this.onChooseExistingClick}>
							{copy.existingTravelerBtn}
						</button>
						<span class={s.introBtnExplainer}>{copy.existingTravelerDescription}</span>
					</div>
				</div>
			</div>
		);
	}
	renderDigitsGroup(inputType, groupIndex) {
		const isFocused = this.state[`${inputType}InputFocused`];
		const group = new Array(4).fill('x').map((x, i) => (
			<div class={joinClasses(s.digitInput, s[inputType], isFocused && s.focused)} key={`${inputType}-${groupIndex}-${i}`}>
				{this.state[`${inputType}Input`]?.[groupIndex * 4 + i] || ''}
			</div>
		));
		return <div class={s.digitInputGroup}>{group}</div>;
	}

	renderInputStage({ copy, isTheatre }) {
		const { client } = this.props;
		const {
			walletPlatform,
			existingTraveler,
			confirmationCodeInput,
			loading,
			hasConfirmationCodeError,
			hasPassIdError,
			hasMaximumBadgeError,
			hasWrongPassFlow,
			apiErrorMessage,
		} = this.state;
		const displayPlatformChoice = client.device === 'desktop' && !existingTraveler;
		let codeInputText = existingTraveler ? copy.codeInputExisting : copy.codeInputNew;
		if (!isTheatre) {
			codeInputText = copy.codeInputHomeEnt;
		}
		const helpText = existingTraveler ? copy.helpTextExisting : copy.helpTextNew;

		return [
			<div class={joinClasses(s.content, s.codeInputContainer)} onClick={() => this.$codeInput.focus()}>
				<HTML class={s.codeInputText} content={codeInputText} markdown />
				<div class={s.codeInput}>
					{this.renderDigitsGroup('confirmationCode', 0)}
					<span class={s.inputDash}>-</span>
					{this.renderDigitsGroup('confirmationCode', 1)}
				</div>
				<input
					class={s.hiddenCodeInput}
					id="confirmationCodeInput"
					ref={($ref) => (this.$codeInput = $ref)}
					type="text"
					onInput={this.onCodeInput}
					value={confirmationCodeInput}
					onFocus={() => this.setState({ confirmationCodeInputFocused: true })}
					onBlur={() => this.setState({ confirmationCodeInputFocused: false })}
				/>
				<span class={joinClasses(s.errorMessage, (hasConfirmationCodeError || hasWrongPassFlow) && s.show)}>{apiErrorMessage || copy.codeError}</span>
			</div>,
			existingTraveler && (
				<div class={joinClasses(s.content, s.passIdInputContainer)} onClick={() => this.$passIdInput.focus()}>
					<HTML class={s.passIdInputText} content={copy.passIdInput} markdown />
					<div class={s.passIdInput}>
						{this.renderDigitsGroup('passId', 0)}
						<span class={s.inputDash}>-</span>
						{this.renderDigitsGroup('passId', 1)}
						<span class={s.inputDash}>-</span>
						{this.renderDigitsGroup('passId', 2)}
					</div>
					<input
						class={s.hiddenCodeInput}
						id="passIdInput"
						ref={($ref) => (this.$passIdInput = $ref)}
						type="text"
						onInput={this.onPassIdInput}
						value={this.state.passIdInput}
						onFocus={() => this.setState({ passIdInputFocused: true })}
						onBlur={() => this.setState({ passIdInputFocused: false })}
					/>
					<span class={joinClasses(s.errorMessage, hasPassIdError && s.show)}>
						{hasMaximumBadgeError ? copy.maxRedemptionError : copy.passIdError}
					</span>
				</div>
			),
			displayPlatformChoice && (
				<div class={joinClasses(s.content, s.platformChoiceContainer)}>
					<HTML class={s.platformChoiceCTA} content={isTheatre ? copy.platformChoiceCTA : copy.platformChoiceCTAHomeEnt} markdown />
					<div class={s.platformChoiceButtons}>
						<button
							class={joinClasses(s.chooseAppleButton, walletPlatform === 'ios' && s.selected)}
							onClick={() => this.changeWalletPlatform('ios')}
						>
							<AppleLogo class={s.appleChoiceLogo} />
							<span>{copy.apple}</span>
						</button>
						<button
							class={joinClasses(s.chooseGoogleButton, walletPlatform === 'android' && s.selected)}
							onClick={() => this.changeWalletPlatform('android')}
						>
							<GoogleLogo class={s.googleChoiceLogo} />
							<span>{copy.google}</span>
						</button>
					</div>
				</div>
			),
			isTheatre && (
				<div class={joinClasses(s.content, s.helpContainer)}>
					<HTML class={s.helpText} content={helpText} markdown />
					<a class={s.buyTicketLink} href={copy.buyTicketLink} target="_blank">
						{copy.buyTicketCTA}
					</a>
				</div>
			),
			<div class={joinClasses(s.loadingOverlay, loading && s.show)}>
				<div class={s.spinner} />
			</div>,
		];
	}

	renderConfirmation({ copy, isTheatre }) {
		const { client, imageRegionRoot } = this.props;
		const { existingTraveler, newPassDownloadLink, walletPlatform, isWebView, badgeSuccessMaximum, showLinkCopiedConfirmation } = this.state;
		const isDesktop = client.device === 'desktop';
		const showQR = !existingTraveler && isDesktop;
		const showWalletLinks = !existingTraveler && !isDesktop;
		const useCopyDownloadLink = showWalletLinks && walletPlatform === 'ios' && isWebView;

		return (
			<div class={joinClasses(s.content, existingTraveler && s.confirmationExisting)}>
				{existingTraveler && (
					<HTML class={s.confirmationExisting} content={badgeSuccessMaximum ? copy.badgeAddedMaximum : copy.badgeAddedExisting} markdown />
				)}
				{showQR && [
					<p class={s.scanQRTitle}>{isTheatre ? copy.scanQRTitle : copy.scanQRTitleHomeEnt}</p>,
					<div class={s.scanQRContainer}>
						<div class={s.qrContainer}>
							<img class={s.qr} src={this.$qrCanvas?.toDataURL()} alt="Boarding Pass QR Code" />
						</div>
						<HTML class={s.scanQRInstructions} content={isTheatre ? copy.scanQR : copy.scanQRHomeEnt} markdown />
					</div>,
				]}
				{showWalletLinks && [
					<HTML class={s.downloadLinkDescription} content={isTheatre ? copy.addToWallet : copy.addToWalletHomeEnt} markdown />,
					<div class={s.downloadBtnContainer}>
						<a
							class={s.downloadButton}
							href={newPassDownloadLink}
							target="_blank"
							onClick={(e) => this.onAddToWalletClick({ e, walletPlatform, useCopyDownloadLink, link: newPassDownloadLink })}
						>
							<img
								src={`${walletPlatform === 'ios' ? `${imageRegionRoot}/airport/add-to-apple-wallet.webp` : `${imageRegionRoot}/airport/add-to-google-wallet.webp`}`}
								class={s.walletLogo}
							/>
						</a>
					</div>,
					showLinkCopiedConfirmation && <p class={s.linkCopiedConfirmation}>{copy.linkCopied}</p>,
				]}
			</div>
		);
	}

	render(props) {
		const { stage } = this.state;
		const { copy } = props?.section?.popUp || {};
		return (
			<div class={joinClasses(s.scroller, stage === INTRO && s.scrollerLarge)}>
				<div class={s.contentContainer} onMouseDown={(e) => e.stopPropagation()} onTouchStart={(e) => e.stopPropagation()}>
					{stage === INTRO && (
						<img class={s.passBanner} src={`${props.imageRoot}/airport/hCbFE9.webp`} alt="Image of e-passes on a starry purple background." />
					)}
					{this.renderRestartButton({ copy })}
					{stage === INTRO && this.renderIntro({ copy })}
					{stage === THEATRES_INTRO && this.renderTheatreIntro({ copy })}
					{stage === THEATRES_INPUT && this.renderInputStage({ copy, isTheatre: true })}
					{stage === THEATRES_CONFIRMATION && this.renderConfirmation({ copy, isTheatre: true })}
					{stage === HOME_ENT_INPUT && this.renderInputStage({ copy, isTheatre: false })}
					{stage === HOME_ENT_CONFIRMATION && this.renderConfirmation({ copy, isTheatre: false })}
					<canvas class={s.hiddenCanvas} ref={($ref) => (this.$qrCanvas = $ref)} />
				</div>
			</div>
		);
	}
}
